import { useState } from "react";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";
import {
  useListOtcAmendmentReviews,
  OtcAmendmentReviewListItem,
} from "@/lib/api/otcAmendmentReview";
import { ContractTypeOptions, CommodityOptions } from "@/lib/api/otc";
import { DataTable, DataTableColumn } from "@/components/ui/DataTable/index";
import { AmendmentReviewDetailModal } from "./AmendmentReviewDetailModal";
import { generateAmendmentTitleForLayer } from "./contract-detail-view/common";

function pendingLayer(item: OtcAmendmentReviewListItem) {
  return item.layers.find((layer) => layer.id === item.pending_layer_id) ?? null;
}

const columns: DataTableColumn<OtcAmendmentReviewListItem>[] = [
  {
    id: "contract_no",
    accessorFn: (row: OtcAmendmentReviewListItem) => row.contract.contract_no ?? "",
    header: "Contract #",
  },
  {
    id: "customer",
    accessorFn: (row: OtcAmendmentReviewListItem) => row.contract.Customer?.long_name ?? "",
    header: "Customer",
  },
  {
    id: "trader",
    accessorFn: (row: OtcAmendmentReviewListItem) => row.contract.Trader?.name ?? "",
    header: "Trader",
  },
  {
    id: "commodity",
    accessorFn: (row: OtcAmendmentReviewListItem) => {
      const commodity = row.contract.commodity ?? "";
      return (
        CommodityOptions.find(
          (option) => option.value?.toLocaleLowerCase() === commodity?.toLocaleLowerCase()
        )?.label || commodity
      );
    },
    header: "Commodity",
  },
  {
    id: "contract_type",
    accessorFn: (row: OtcAmendmentReviewListItem) => {
      const contractType = row.contract.contract_type ?? "";
      return (
        ContractTypeOptions.find((item) => item.value === contractType)?.label || contractType
      );
    },
    header: "Contract Type",
  },
  {
    id: "layer_no",
    accessorFn: (row: OtcAmendmentReviewListItem) => pendingLayer(row)?.layer_no ?? "",
    header: "Layer #",
  },
  {
    id: "amendment_type",
    accessorFn: (row: OtcAmendmentReviewListItem) => {
      const layer = pendingLayer(row);
      return layer ? generateAmendmentTitleForLayer(layer) : "";
    },
    header: "Amendment Type",
  },
  {
    id: "created_at",
    accessorFn: (row: OtcAmendmentReviewListItem) => pendingLayer(row)?.created_at ?? "",
    header: "Created At",
    columnFilterType: "date",
    filterDateTimezone: CommonDestinationTimezone,
    filterDisplayFn: (v: unknown) => moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
    filterParseDateFn: (v: unknown) => moment.tz(v as string, CommonDestinationTimezone), 
    cell: ({ row }: { row: { original: OtcAmendmentReviewListItem } }) => {
      const created = pendingLayer(row.original)?.created_at;
      return (
        <span className="text-gray-600 text-sm">
          {created ? moment.tz(created, CommonDestinationTimezone).format("MM/DD/YYYY") : ""}
        </span>
      );
    },
  },
  {
    id: "task_progress",
    accessorFn: (row: OtcAmendmentReviewListItem) =>
      `${row.completed_task_count}/${row.total_task_count}`,
    header: "Tasks",
  },
];

export function OtcAmendmentReviewSection() {
  const reviewsRes = useListOtcAmendmentReviews();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const reviews = reviewsRes?.data?.data || [];
  const isLoading = reviewsRes?.isLoading;

  // Derive the selected item from the live query data so it stays fresh
  // after task completion invalidates the query.
  const selected = selectedId
    ? reviews.find((r: OtcAmendmentReviewListItem) => r.pending_layer_id === selectedId) ?? null
    : null;

  return (
    <div className="w-full">
      <AmendmentReviewDetailModal
        item={selected}
        open={selectedId !== null}
        onClose={() => setSelectedId(null)}
      />

      <DataTable
        stickyHeaders
        tableClassName="max-h-[calc(100vh-400px)] overflow-y-scroll"
        data={reviews}
        columns={columns}
        enableSorting
        enableMultiSort
        enableFiltering
        enableColumnVisibility
        filterStyle="select"
        filterMode="internal"
        emptyMessage={isLoading ? "Loading..." : "No amendments pending review"}
        rowClickable={true}
        onRowClick={(row: OtcAmendmentReviewListItem) => setSelectedId(row.pending_layer_id)}
      />
    </div>
  );
}
